import { CampaignType, RedirectChannel, RedirectProtocol, RedirectCampaignType } from '../constants/ABA';
import { ParsedRedirect, ServiceToServiceRedirect, AllPartialExcept } from '../types';
import { CHANGE_CASHIER_RESULT_URL, REDIRECT_VIA_DEEP_LINK, REDIRECT_VIA_PLAIN_DEEP_LINK } from '../constants';

/**
 * Recommended to use for redirect to deeplink
 * @param {string} payload
 * @return Action
 */
export const redirectViaPlainDeepLink = (payload: string) => ({
  type: REDIRECT_VIA_PLAIN_DEEP_LINK,
  payload,
});

// LOW LEVEL ACTION CREATORS

/**
 * Low-level action creator. Recommended to use if other action creators are not suitable
 * @param {ParsedRedirect} payload
 * @return Action
 */
export const redirectCommon = (payload: ParsedRedirect) => ({
  type: REDIRECT_VIA_DEEP_LINK,
  payload,
});

/**
 * Low-level action creator. Recommended to use if other action creators are not suitable
 * @param {ParsedRedirect} payload
 * @return Action
 */
export const changeCashierURLCommon = (payload: ParsedRedirect | null) => ({
  type: CHANGE_CASHIER_RESULT_URL,
  payload,
});

// HIGH LEVEL ACTION CREATORS

export type RedirectViaCampaignType = {
  to: RedirectCampaignType;
  param: string;
  permanent: boolean;
  channel: RedirectChannel;
  protocol: string;
};

export type RedirectAbsolute = {
  to: string;
  permanent: boolean;
  channel: RedirectChannel;
  protocol: string;
};

export type RedirectServiceToService = {
  to: ServiceToServiceRedirect;
  permanent: boolean;
  channel: RedirectChannel;
  protocol: string;
};

/**
 * High-level action creator. Recommended to use for navigate between services via campaign_type
 */
export const redirectViaCampaignType = ({
  to,
  param = '',
  permanent = false,
  channel = RedirectChannel.NativeApp,
  protocol = RedirectProtocol,
}: AllPartialExcept<RedirectViaCampaignType, 'to'>) =>
  redirectCommon(getParsedRedirectViaCampaignType(to, param, permanent, channel, protocol));

/**
 * High-level action creator. Recommended to use for change cashier URL via campaign_type
 */
export const changeCashierURLViaCampaignType = ({
  to,
  param = '',
  permanent = false,
  channel = RedirectChannel.NativeApp,
  protocol = RedirectProtocol,
}: AllPartialExcept<RedirectViaCampaignType, 'to'>) =>
  changeCashierURLCommon(getParsedRedirectViaCampaignType(to, param, permanent, channel, protocol));

/**
 * High-level action creator. Recommended to use for navigate to external resource
 */
export const redirectAbsolute = ({
  to,
  permanent = false,
  channel = RedirectChannel.NativeApp,
  protocol = RedirectProtocol,
}: AllPartialExcept<RedirectAbsolute, 'to'>) =>
  redirectCommon(getParsedRedirectAbsolute(to, permanent, channel, protocol));

/**
 * High-level action creator. Recommended to use for change cashier URL to external resource
 */
export const changeCashierURLAbsolute = ({
  to,
  permanent = false,
  channel = RedirectChannel.NativeApp,
  protocol = RedirectProtocol,
}: AllPartialExcept<RedirectAbsolute, 'to'>) =>
  changeCashierURLCommon(getParsedRedirectAbsolute(to, permanent, channel, protocol));

/**
 * High-level action creator. Recommended to use for navigation between services via service name
 */
export const redirectServiceToService = ({
  to,
  permanent = false,
  channel = RedirectChannel.NativeApp,
  protocol = RedirectProtocol,
}: AllPartialExcept<RedirectServiceToService, 'to'>) =>
  redirectCommon(getParsedRedirectServiceToService(to, permanent, channel, protocol));

/**
 * High-level action creator. Recommended to use for change cashier URL via service name
 */
export const changeCashierURLServiceToService = ({
  to,
  permanent = false,
  channel = RedirectChannel.NativeApp,
  protocol = RedirectProtocol,
}: AllPartialExcept<RedirectServiceToService, 'to'>) =>
  changeCashierURLCommon(getParsedRedirectServiceToService(to, permanent, channel, protocol));

/**
 * Utils for getting parsed redirect
 */

const getParsedRedirectViaCampaignType = (
  to: RedirectCampaignType,
  param: string = '',
  permanent: boolean = false,
  channel: RedirectChannel = RedirectChannel.NativeApp,
  protocol: string = RedirectProtocol
): ParsedRedirect => ({
  channel,
  protocol,
  campaign_param: param,
  campaign_type: to,
  campaign_permanent: permanent,
});

const getParsedRedirectAbsolute = (
  to: string,
  permanent: boolean = false,
  channel: RedirectChannel = RedirectChannel.NativeApp,
  protocol: string = RedirectProtocol
): ParsedRedirect => ({
  channel,
  protocol,
  campaign_param: to,
  campaign_permanent: permanent,
  campaign_type: CampaignType.Absolute,
});

const getParsedRedirectServiceToService = (
  to: ServiceToServiceRedirect,
  permanent: boolean = false,
  channel: RedirectChannel = RedirectChannel.NativeApp,
  protocol: string = RedirectProtocol
): ParsedRedirect => ({
  channel,
  protocol,
  campaign_param: to,
  campaign_permanent: permanent,
  campaign_type: CampaignType.Service,
});
